import { useState } from 'react';
import { Badge, Button, EmptyState, Input, Menu } from '../../components/ui';
import {
  formatDate,
  InlineTextInput,
  isOverdue,
  memberLabel,
  statusTone,
} from '../listView/TaskTable';
import { TASK_PRIORITY_LABELS } from '../tasks/types';
import {
  buildBoardColumns,
  buildSectionColumns,
  buildUnifiedColumns,
  dropIntent,
  UNCATEGORIZED_COLUMN_NAME,
  unifiedMoveOptions,
} from './boardLens';
import type { DragEvent } from 'react';
import { FieldChips } from '../fields/FieldPicker';
import type { FieldChip } from '../fields/fieldChips';
import type { MenuItem } from '../../components/ui';
import type { ProjectMemberWithProfile, ProjectStatus } from '../projects/types';
import type { SavedViewSort } from '../savedViews/types';
import type { Section } from '../sections/types';
import type { Task } from '../tasks/types';
import type { BoardColumn, BoardGrouping, SubtaskProgress } from './boardLens';

/**
 * Board View (handoff 18): active statuses as columns, one card per
 * top-level task, indicators matching the List View rows (assignee, due
 * date, priority, subtask progress, comment and attachment counts).
 * Clicking a card opens the same task details panel the List View uses.
 *
 * Stage 2 makes the Board write one thing: dragging a card to another
 * column changes its status — nothing else (locked decision: no persisted
 * board order; cards follow the toolbar sort). Each card also carries a
 * "Move to" menu, so the same move works without a mouse. Dropping on the
 * Uncategorized column clears the status, matching what the menu offers.
 *
 * Nothing here decides permission: canEdit hides the drag affordances as a
 * convenience, and Row Level Security refuses any move the caller may not
 * make (the optimistic card is rolled back by the hook). The
 * supplier-review indicator arrives with Phase 6.
 */

const EMPTY_CHIPS: Map<string, FieldChip[]> = new Map();

interface BoardViewProps {
  /** Filtered top-level tasks; the Board buckets them by status. */
  tasks: Task[];
  statuses: ProjectStatus[];
  sort: SavedViewSort;
  members: ProjectMemberWithProfile[];
  progress: Record<string, SubtaskProgress>;
  commentCounts: Record<string, number>;
  fileCounts: Record<string, number>;
  /**
   * The project's custom field values, keyed by task.
   *
   * Optional, and empty for every project that has no fields — which is all of
   * them except the ones built from a template that defines some. Fetched once
   * for the whole board by the caller, never per card.
   */
  fieldChips?: Map<string, FieldChip[]>;
  /** May move cards: drag and the per-card menu appear only when true. */
  canEdit: boolean;
  onOpenTask: (task: Task) => void;
  /** Null statusId means Uncategorized — the task's status is cleared. */
  onMoveTask: (taskId: string, statusId: string | null) => void;

  /** Which field the columns stand for. */
  grouping: BoardGrouping;
  /** The project's sections, for the section grouping. */
  sections: Section[];
  /** Null sectionId means No section — the task's section is cleared. */
  onMoveTaskToSection: (taskId: string, sectionId: string | null) => void;

  /**
   * 'grouped' is the board every project has had: columns follow `grouping`,
   * and completion is not something the board touches.
   *
   * 'unified' is the Personal Tasks board: columns are always statuses, with a
   * permanent Completed column that reflects the List View checkbox. Sections
   * are a filter above the board rather than a second column layout, so
   * `grouping` is ignored.
   */
  layout?: 'grouped' | 'unified';
  /**
   * Required by the unified layout: a drop writes status AND completion
   * together. Absent in the grouped layout, which writes one field per drop.
   */
  onMoveTaskToColumn?: (
    taskId: string,
    intent: { statusId: string | null; isCompleted: boolean },
  ) => void;
  /**
   * Section management from a column header. Absent when the viewer may not
   * manage sections, which is a different permission from moving a card.
   */
  onRenameSection?: (sectionId: string, name: string) => void;
  onCreateSection?: (name: string) => void;
  /**
   * Renaming the TASK on a card. A different thing from onRenameSection
   * above, which renames the column, and the two are easy to confuse — hence
   * the Task in the name.
   *
   * Kyu, 14 September: "We need to be able to rename tasks we've already
   * created." Renaming worked in the List view row menu and nowhere else, so
   * anybody working on the Board had to change view to fix a name.
   *
   * Optional: absent, the card menu is exactly what it was.
   */
  onRenameTask?: (task: Task, changes: { title: string }) => void;
}

const DRAG_MIME = 'text/plain';

function BoardCard({
  task,
  members,
  progress,
  commentCount,
  fileCount,
  fieldChips,
  moveItems,
  canEdit,
  onOpen,
  onRename,
}: {
  task: Task;
  members: ProjectMemberWithProfile[];
  progress: SubtaskProgress | undefined;
  commentCount: number;
  fileCount: number;
  /**
   * Where this card can go, already worked out. Built by the column rather
   * than here because the two layouts answer it differently — statuses in the
   * grouped board, the columns themselves in the unified one — and a card that
   * re-derived it would be a second place for the rules to drift.
   */
  moveItems: MenuItem[];
  /**
   * The project's custom field values for this card, already ordered.
   *
   * Passed in rather than fetched here: the whole board's values arrive in one
   * call, and a card that fetched its own would open one connection per card.
   */
  fieldChips: FieldChip[];
  canEdit: boolean;
  onOpen: () => void;
  /** Absent when the board has no writer, which hides the Rename item. */
  onRename?: ((task: Task, changes: { title: string }) => void) | undefined;
}) {
  const [renaming, setRenaming] = useState(false);
  const assignee = members.find((member) => member.user_id === task.assignee_id);
  const overdue = isOverdue(task);
  const hasMeta = assignee !== undefined || task.due_date !== null || task.priority !== null;
  const indicators = [
    progress && progress.total > 0
      ? `${progress.done} of ${progress.total} ${progress.total === 1 ? 'subtask' : 'subtasks'}`
      : null,
    commentCount > 0 ? `${commentCount} ${commentCount === 1 ? 'comment' : 'comments'}` : null,
    fileCount > 0 ? `${fileCount} ${fileCount === 1 ? 'file' : 'files'}` : null,
  ].filter((line): line is string => line !== null);

  const canRename = canEdit && onRename !== undefined;
  /* Rename first, moves after. The list is read top down and renaming is the
     thing somebody came to the menu for on purpose, where a move is often the
     drag they could not be bothered to do. */
  const menuItems: MenuItem[] = canRename
    ? [{ label: 'Rename', onSelect: () => setRenaming(true) }, ...moveItems]
    : moveItems;

  /* The input replaces the whole card button while it is open.
     A card is one big click target that opens the task, so an input nested
     inside it would be a text field that swallows clicks meant for its own
     parent. Taking the button out for the duration is simpler than fighting
     that, and the card keeps its height either way. */
  if (renaming && onRename !== undefined) {
    return (
      <li className="board-card">
        <div className="board-card-renaming">
          <InlineTextInput
            value={task.title}
            inputLabel={`Title of ${task.title}`}
            allowEmpty={false}
            onSave={(next) => onRename(task, { title: next })}
            onClose={() => setRenaming(false)}
          />
        </div>
      </li>
    );
  }

  return (
    <li className="board-card">
      <button
        type="button"
        className="board-card-button"
        aria-label={`Open task ${task.title}`}
        draggable={canEdit}
        onDragStart={
          canEdit
            ? (event: DragEvent<HTMLButtonElement>) => {
                event.dataTransfer.setData(DRAG_MIME, task.id);
                event.dataTransfer.effectAllowed = 'move';
              }
            : undefined
        }
        onClick={onOpen}
      >
        <span
          className={
            task.is_completed ? 'board-card-title board-card-title--completed' : 'board-card-title'
          }
        >
          {task.title}
        </span>

        {/* The meta line and the indicator line are ALWAYS drawn, empty or
            not. Rendered only when they had something to say, a column of
            cards ran from one line to four and the board read as a pile —
            the same thing Kyu named on the Deals cards. An empty line holds
            its height; the card stays the height of every other card. */}
        <span className="board-card-meta" data-empty={hasMeta ? undefined : 'true'}>
          {assignee ? <span className="board-card-assignee">{memberLabel(assignee)}</span> : null}
          {task.due_date !== null ? (
            <span className={overdue ? 'board-card-due board-card-due--overdue' : 'board-card-due'}>
              {formatDate(task.due_date)}
              {overdue ? ' · Overdue' : ''}
            </span>
          ) : null}
          {task.priority !== null ? (
            <span className={`board-card-priority board-card-priority--${task.priority}`}>
              {TASK_PRIORITY_LABELS[task.priority]}
            </span>
          ) : null}
        </span>

        <span className="board-card-indicators">{indicators.join(' · ')}</span>

        <FieldChips chips={fieldChips} />
      </button>

      {/* The menu now survives a card with nowhere to move, because Rename is
          reason enough to open it. The trigger's name says what the menu holds
          rather than naming one item, so a card that can only be renamed is not
          announced as a move. */}
      {canEdit && menuItems.length > 0 ? (
        <div className="board-card-menu">
          <Menu
            trigger={<span aria-hidden="true">•••</span>}
            triggerLabel={`Actions for ${task.title}`}
            items={menuItems}
          />
        </div>
      ) : null}
    </li>
  );
}

function Column({
  column,
  members,
  progress,
  commentCounts,
  fileCounts,
  fieldChips,
  moveItemsFor,
  canEdit,
  onOpenTask,
  onDropColumn,
  onRename,
  onRenameTask,
}: {
  column: BoardColumn;
  members: ProjectMemberWithProfile[];
  progress: Record<string, SubtaskProgress>;
  commentCounts: Record<string, number>;
  fileCounts: Record<string, number>;
  /** Every card's field values, keyed by task. Empty on a project with none. */
  fieldChips: Map<string, FieldChip[]>;
  moveItemsFor: (task: Task) => MenuItem[];
  canEdit: boolean;
  onOpenTask: (task: Task) => void;
  /** Writes whatever a drop on this column means. */
  onDropColumn: (taskId: string, column: BoardColumn) => void;
  /** Renames the COLUMN. onRenameTask below renames a card. */
  onRename?: (sectionId: string, name: string) => void;
  onRenameTask?: ((task: Task, changes: { title: string }) => void) | undefined;
}) {
  const [dragOver, setDragOver] = useState(false);
  const [renaming, setRenaming] = useState(false);
  const [draft, setDraft] = useState(column.name);

  const dropHandlers = canEdit
    ? {
        onDragOver: (event: DragEvent<HTMLElement>) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'move';
          setDragOver(true);
        },
        onDragLeave: () => setDragOver(false),
        onDrop: (event: DragEvent<HTMLElement>) => {
          event.preventDefault();
          setDragOver(false);
          const taskId = event.dataTransfer.getData(DRAG_MIME);
          if (taskId !== '') {
            onDropColumn(taskId, column);
          }
        },
      }
    : {};

  return (
    <section
      className={[
        'board-column',
        column.isCompletedColumn === true ? 'board-column--completed' : '',
        dragOver ? 'board-column--drop' : '',
      ]
        .filter((name) => name !== '')
        .join(' ')}
      aria-label={`${column.name} column`}
      {...dropHandlers}
    >
      <header className="board-column-header">
        {renaming && column.canRename && onRename !== undefined && column.groupId !== null ? (
          <form
            className="board-column-rename"
            onSubmit={(event) => {
              event.preventDefault();
              const name = draft.trim();
              // An empty name would leave a column nobody can identify, so
              // the form simply closes without writing.
              if (name !== '' && name !== column.name) {
                onRename(column.groupId ?? '', name);
              }
              setRenaming(false);
            }}
          >
            <Input
              label={`Rename ${column.name}`}
              labelHidden
              autoFocus
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              // Escape is the way out. Blur alone was not enough: clicking
              // empty page space moves focus to <body> and left the field
              // open with no way to cancel except pressing Enter, which
              // writes. Found by clicking it in a browser.
              onKeyDown={(event) => {
                if (event.key === 'Escape') {
                  event.preventDefault();
                  setDraft(column.name);
                  setRenaming(false);
                }
              }}
              onBlur={() => setRenaming(false)}
            />
          </form>
        ) : column.canRename && onRename !== undefined ? (
          <button
            type="button"
            className="board-column-rename-trigger"
            aria-label={`Rename section ${column.name}`}
            onClick={() => {
              setDraft(column.name);
              setRenaming(true);
            }}
          >
            <Badge tone={statusTone(column.colorKey)}>{column.name}</Badge>
          </button>
        ) : (
          <Badge tone={statusTone(column.colorKey)}>{column.name}</Badge>
        )}
        <span className="board-column-count">
          {column.tasks.length} {column.tasks.length === 1 ? 'task' : 'tasks'}
        </span>
      </header>
      {column.tasks.length === 0 ? (
        <p className="board-column-empty">No tasks</p>
      ) : (
        <ul className="board-column-cards">
          {column.tasks.map((task) => (
            <BoardCard
              key={task.id}
              task={task}
              members={members}
              progress={progress[task.id]}
              commentCount={commentCounts[task.id] ?? 0}
              fileCount={fileCounts[task.id] ?? 0}
              moveItems={moveItemsFor(task)}
              fieldChips={fieldChips.get(task.id) ?? []}
              canEdit={canEdit}
              onOpen={() => onOpenTask(task)}
              onRename={onRenameTask}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

export function BoardView({
  tasks,
  statuses,
  sort,
  members,
  progress,
  commentCounts,
  fileCounts,
  fieldChips,
  canEdit,
  onOpenTask,
  onMoveTask,
  grouping,
  sections,
  onMoveTaskToSection,
  onRenameSection,
  onCreateSection,
  onRenameTask,
  layout = 'grouped',
  onMoveTaskToColumn,
}: BoardViewProps) {
  const [adding, setAdding] = useState(false);
  const [newName, setNewName] = useState('');

  // One empty map for every board without fields, rather than a fresh one per
  // render: Column takes it as a prop and a new object each time would defeat
  // any memo somebody adds later.
  const chips = fieldChips ?? EMPTY_CHIPS;

  const unified = layout === 'unified';
  const columns = unified
    ? buildUnifiedColumns(tasks, statuses, sort)
    : grouping === 'section'
      ? buildSectionColumns(tasks, sections, sort)
      : buildBoardColumns(tasks, statuses, sort);

  const statusTargets = statuses
    .filter((status) => status.is_active)
    .slice()
    .sort((left, right) => left.position - right.position);

  /**
   * Where a card can go.
   *
   * The unified board reads the columns it just built, so Completed is offered
   * and the folded-away completed status is not. The grouped board keeps the
   * status list it has always used — including when it is grouped by section,
   * where the menu deliberately still moves status rather than section.
   */
  function moveItemsFor(task: Task): MenuItem[] {
    if (unified) {
      return unifiedMoveOptions(task, columns).map((option) => ({
        label: option.label,
        onSelect: () =>
          onMoveTaskToColumn?.(task.id, {
            statusId: option.statusId,
            isCompleted: option.isCompleted,
          }),
      }));
    }
    return [
      ...statusTargets
        .filter((status) => status.id !== task.status_id)
        .map((status) => ({
          label: `Move to ${status.name}`,
          onSelect: () => onMoveTask(task.id, status.id),
        })),
      ...(task.status_id !== null
        ? [
            {
              label: `Move to ${UNCATEGORIZED_COLUMN_NAME}`,
              onSelect: () => onMoveTask(task.id, null),
            },
          ]
        : []),
    ];
  }

  /**
   * A drop, translated to a write.
   *
   * Unified: both fields at once (dropIntent), which is what stops completion
   * and status drifting apart. Grouped by section: section_id. Grouped by
   * status: status_id, and completion is left alone.
   */
  function dropOnColumn(taskId: string, column: BoardColumn) {
    if (unified) {
      onMoveTaskToColumn?.(taskId, dropIntent(column));
      return;
    }
    if (grouping === 'section') {
      onMoveTaskToSection(taskId, column.groupId);
      return;
    }
    onMoveTask(taskId, column.groupId);
  }

  if (columns.length === 0) {
    return (
      <EmptyState
        title="No statuses yet"
        description="This project has no active statuses, so the Board has no columns to show."
      />
    );
  }

  return (
    <div className="board-view">
      {columns.map((column) => (
        <Column
          key={column.key}
          column={column}
          members={members}
          progress={progress}
          commentCounts={commentCounts}
          fileCounts={fileCounts}
          fieldChips={chips}
          moveItemsFor={moveItemsFor}
          canEdit={canEdit}
          onOpenTask={onOpenTask}
          onDropColumn={dropOnColumn}
          onRenameTask={onRenameTask}
          {...(onRenameSection === undefined || unified ? {} : { onRename: onRenameSection })}
        />
      ))}

      {/* Only in section mode. Statuses have no create anywhere in the app —
          they are made by the database when a project is created — so an Add
          column button on the status board would be a control with nothing
          behind it. */}
      {!unified && grouping === 'section' && onCreateSection !== undefined ? (
        <section className="board-column board-column--add">
          {adding ? (
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const name = newName.trim();
                if (name !== '') {
                  onCreateSection(name);
                }
                setNewName('');
                setAdding(false);
              }}
            >
              <Input
                label="New section"
                labelHidden
                autoFocus
                placeholder="Section name"
                value={newName}
                onChange={(event) => setNewName(event.target.value)}
                onBlur={() => setAdding(false)}
              />
            </form>
          ) : (
            <Button variant="ghost" onClick={() => setAdding(true)}>
              + Add column
            </Button>
          )}
        </section>
      ) : null}
    </div>
  );
}
